import { inject, Injectable } from '@angular/core';
import {
  AreaManagementUsageTableViewModel,
  AreaReturnSbuUsageTableViewModel,
  ChangeUsageCostCenterRequest,
  CostCenterClient,
  CostCenterViewModel,
  IncidentClient,
  IncidentIdResult,
  RequestFileWrapperOfCostCenterAreaReturnTicketRequest,
  RequestFileWrapperOfSbuAreaReturnTicketRequest,
} from '@core/api';
import { BehaviorSubject, Observable } from 'rxjs';

export interface IAreaReturnSbuUsageTableViewModel
  extends AreaReturnSbuUsageTableViewModel {
  requestedUsageUntil: Date;
  numberOfWorkspacesSelection: number;
}

@Injectable({
  providedIn: 'root',
})
export class AreaManagementService {
  readonly costCenterClient = inject(CostCenterClient);
  readonly incidentClient = inject(IncidentClient);

  private rows: any[] = [];
  readonly rowUpdated$ = new BehaviorSubject<any[]>(this.rows);
  private rowsVb: any[] = [];
  readonly rowVbUpdated$ = new BehaviorSubject<any[]>(this.rowsVb);

  readonly loadingDataSubject$ = new BehaviorSubject<boolean>(false);

  addRow(row: any) {
    this.rows = [...this.rows, row];
    this.rowUpdated$.next(this.rows);
  }

  addRowVb(row: any) {
    this.rowsVb = [...this.rowsVb, row];
    this.rowVbUpdated$.next(this.rowsVb);
  }

  deleteRow(id: any) {
    this.rows = this.rows.filter(row => row.rowData.id !== id);
    this.rowUpdated$.next(this.rows);
  }

  deleteRowVb(id: any) {
    this.rowsVb = this.rowsVb.filter(row => row.rowData.id !== id);
    this.rowVbUpdated$.next(this.rowsVb);
  }

  resetRows() {
    this.rows = [];
    this.rowUpdated$.next([]);
    this.rowsVb = [];
    this.rowVbUpdated$.next([]);
  }

  getCostCenters(): Observable<CostCenterViewModel[]> {
    return this.costCenterClient.getCostCenters();
  }

  getUsagesForLocationAndCostcenter(
    locationId: string | undefined,
    costcenterId: string | undefined
  ): Observable<AreaManagementUsageTableViewModel[]> {
    return this.costCenterClient.getUsagesForLocationAndCostcenter(
      locationId,
      costcenterId
    );
  }

  getCostcenterUsagesForChange(
    locationId: string | undefined,
    costcenterId?: string | undefined
  ): Observable<AreaManagementUsageTableViewModel[]> {
    return this.costCenterClient.getCostcenterUsagesForChange(
      locationId,
      costcenterId
    );
  }

  changeCostCenterOfUsages(
    request: ChangeUsageCostCenterRequest | undefined
  ): Observable<void> {
    return this.costCenterClient.changeCostCenterOfUsages(request);
  }

  createSbuAreaReturnTicket(
    req: RequestFileWrapperOfSbuAreaReturnTicketRequest
  ): Observable<IncidentIdResult> {
    return this.incidentClient.createSbuAreaReturnTicket(req);
  }

  createCostCenterAreaReturnTicket(
    req: RequestFileWrapperOfCostCenterAreaReturnTicketRequest
  ): Observable<IncidentIdResult> {
    return this.incidentClient.createCostCenterAreaReturnTicket(req);
  }

  getUsageUntil(desiredDateControl): Date {
    const desiredDateValue = desiredDateControl?.value;

    if (desiredDateValue) {
      const parsedDesiredDate = new Date(desiredDateValue);
      return new Date(
        Date.UTC(
          parsedDesiredDate.getUTCFullYear(),
          parsedDesiredDate.getUTCMonth(),
          parsedDesiredDate.getUTCDate()
        )
      );
    }

    return null;
  }

  isDateInISOFormat(dateString: string): boolean {
    return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(dateString);
  }

  parseDate(dateString: string): Date {
    return new Date(dateString);
  }
  parseLocalDate(dateString: string): Date {
    const parts = dateString.split('/');
    const year = +parts[2];
    const month = +parts[1] - 1;
    const day = +parts[0];
    return new Date(Date.UTC(year, month, day));
  }

  onDateChange(
    row: AreaManagementUsageTableViewModel,
    newDate: Date,
    currentRows: AreaManagementUsageTableViewModel[]
  ) {
    const rowIndex = currentRows.findIndex(r => r.id === row.id);

    if (rowIndex !== -1) {
      currentRows[rowIndex] = {
        ...currentRows[rowIndex],
        usageUntil: newDate?.toISOString(),
      };
    } else {
      currentRows.push({ ...row, usageUntil: newDate?.toISOString() });
    }

    return currentRows;
  }

  onDateChangeVb(
    row: IAreaReturnSbuUsageTableViewModel,
    newDate: Date,
    currentRows: IAreaReturnSbuUsageTableViewModel[]
  ): IAreaReturnSbuUsageTableViewModel[] {
    const rowIndex = currentRows.findIndex(r => r.id === row.id);

    if (rowIndex !== -1) {
      currentRows[rowIndex] = {
        ...currentRows[rowIndex],
        requestedUsageUntil: newDate,
      };
    } else {
      currentRows.push({
        ...row,
        requestedUsageUntil: newDate,
        numberOfWorkspacesSelection: 0,
      });
    }

    return currentRows;
  }
}
